import {v4 as uuidv4} from 'uuid';
import {offlineDb} from './db';
import {isOwnedSession} from './offlineSessionManager';
import type {OfflineFile, OfflineFileKind, OfflineSession} from './types';

export interface StoreSessionFileArgs {
  sessionId: string;
  kind: OfflineFileKind;
  refKey: string;
  name: string;
  contentType: string;
  blob: Blob;
}

/** Persists a blob under a session; safe inside an enqueue transaction. */
export const storeSessionFile = async (
  args: StoreSessionFileArgs
): Promise<OfflineFile> => {
  const file: OfflineFile = {
    id: uuidv4(),
    size: args.blob.size,
    createdAt: new Date().toISOString(),
    ...args,
  };
  await offlineDb.files.add(file);
  return file;
};

/** The session's file for `refKey`, if one is stored. */
export const findSessionFile = (
  sessionId: string,
  refKey: string
): Promise<OfflineFile | undefined> =>
  offlineDb.files.where('[sessionId+refKey]').equals([sessionId, refKey]).first();

/** Removes the session's file for `refKey`; a no-op when none is stored. */
export const deleteSessionFile = async (
  sessionId: string,
  refKey: string
): Promise<void> => {
  await offlineDb.files
    .where('[sessionId+refKey]')
    .equals([sessionId, refKey])
    .delete();
};

/** Ready sessions owned by the signed-in user, across every domain context. */
const readyOwnedSessions = async (): Promise<OfflineSession[]> => {
  try {
    const sessions = await offlineDb.sessions
      .where('status')
      .equals('active')
      .toArray();
    return sessions.filter(session => session.ready && isOwnedSession(session));
  } catch {
    return [];
  }
};

/** A cached/captured file for `refKey` in any ready owned session. */
export const findBoundOfflineFile = async (
  refKey: string
): Promise<OfflineFile | undefined> => {
  for (const session of await readyOwnedSessions()) {
    const file = await findSessionFile(session.id, refKey);
    if (file) return file;
  }
  return undefined;
};

// Batched variant: sessions fetched once, keys resolved in parallel per session
// (first session that has a key wins, matching `findBoundOfflineFile`).
export const findBoundOfflineFiles = async (
  refKeys: string[]
): Promise<Map<string, OfflineFile>> => {
  const result = new Map<string, OfflineFile>();
  if (refKeys.length === 0) return result;

  for (const session of await readyOwnedSessions()) {
    const remaining = refKeys.filter(key => !result.has(key));
    if (remaining.length === 0) break;
    const files = await Promise.all(
      remaining.map(refKey => findSessionFile(session.id, refKey))
    );
    files.forEach((file, index) => {
      if (file) result.set(remaining[index], file);
    });
  }

  return result;
};
